import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import { pushSectionResult } from '../utils/fullTestResults';
import ReactCountdownClock from "react-countdown-clock";
import DifficultyBadge from './DifficultyBadge';

function ReadAndSelectComp() {
  const [allItems, setAllItems] = useState([]);
  const [roundItems, setRoundItems] = useState([]);
  const [current, setCurrent] = useState(null);
  const [isStarted, setIsStarted] = useState(false);
  const [submited, setSubmited] = useState(false);
  const [selectedSeconds, setSelectedSeconds] = useState(5);
  const [selectedDifficulty, setSelectedDifficulty] = useState("any");

  const location = useLocation();
  const navigate = useNavigate();
  const isFullTest = (() => { try { return new URLSearchParams(location.search).get('fullTest') === '1'; } catch(e){return false;} })();

  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      const d = params.get('difficulty');
      if (d) setSelectedDifficulty(d);
    } catch (e) {}
  }, [location.search]);

  // Auto-start for Full Test: trigger startRound when requested
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      if (params.get('fullTest') === '1' && !isStarted) {
        const doStart = () => {
          setCorrectList([]);
          setWrongList([]);
          setTotalCorrect(0);
          setTotalIncorrect(0);
          setAnsweredCount(0);
          setSubmited(false);

          const pool = selectedDifficulty === "any" ? [...allItems] : allItems.filter(i => i.difficulty === selectedDifficulty);
          const desired = 15 + Math.floor(Math.random() * 4);
          const count = Math.min(desired, pool.length);
          const indices = new Set();
          while (indices.size < count) indices.add(Math.floor(Math.random() * pool.length));
          const items = Array.from(indices).map(i => pool[i]);
          setRoundItems(items);
          setTotalQuestions(items.length);
          setIsStarted(true);
          setCurrent(null);
        };

        if (allItems && allItems.length > 0) {
          doStart();
        } else {
          const id = setInterval(() => {
            if (allItems && allItems.length > 0) {
              doStart();
              clearInterval(id);
            }
          }, 150);
        }
      }
    } catch (e) {}
  }, [location.search, isStarted, allItems, selectedDifficulty]);

  const [answeredCount, setAnsweredCount] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [totalCorrect, setTotalCorrect] = useState(0);
  const [totalIncorrect, setTotalIncorrect] = useState(0);
  const [correctList, setCorrectList] = useState([]);
  const [wrongList, setWrongList] = useState([]);
  const [selectedChoice, setSelectedChoice] = useState(null); // true = yes, false = no, null = none
  const answeringRef = useRef(false);

  useEffect(() => {
    const load = async () => {
      const res = await fetch("dataReadAndSelect.json");
      const data = await res.json();
      setAllItems(data);
    };
    load();
  }, []);

  const startRound = () => {
    setCorrectList([]);
    setWrongList([]);
    setTotalCorrect(0);
    setTotalIncorrect(0);
    setAnsweredCount(0);
    setSubmited(false);

    const pool = selectedDifficulty === "any" ? [...allItems] : allItems.filter(i => i.difficulty === selectedDifficulty);
    const desired = 15 + Math.floor(Math.random() * 4); // 15-18
    const count = Math.min(desired, pool.length);
    const indices = new Set();
    while (indices.size < count) indices.add(Math.floor(Math.random() * pool.length));
    const items = Array.from(indices).map(i => pool[i]);
    setRoundItems(items);
    setTotalQuestions(items.length);
    setIsStarted(true);
    setCurrent(null);
  };

  useEffect(() => {
    if (isStarted && current === null) nextItem();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStarted]);

  // Auto-advance to next module when results are shown during Full Test
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      if (params.get('fullTest') === '1' && current === undefined && isStarted) {
        try {
          pushSectionResult({ module: 'read-and-select', totalQuestions: totalQuestions || 0, totalCorrect: totalCorrect || 0, totalIncorrect: totalIncorrect || 0, timestamp: Date.now() });
        } catch (e) {}
        const order = ['/read-and-select','/fill-in-the-blanks','/read-and-complete','/interactive-reading','/listening-test','/interactive-listening','/image-test','/interactive-writing','/speak-about-photo','/read-then-speak','/interactive-speaking','/speaking-sample','/writing-sample'];
        const idx = order.indexOf(window.location.pathname);
        const next = idx >= 0 && idx < order.length - 1 ? order[idx + 1] : null;
        if (next) {
          navigate(`${next}?fullTest=1&difficulty=${encodeURIComponent(selectedDifficulty)}`);
        }
      }
    } catch (e) {}
  }, [current, isStarted, location.search, selectedDifficulty, navigate, totalQuestions, totalCorrect, totalIncorrect]);

  const nextItem = () => {
    if (roundItems.length === 0) {
      setCurrent(undefined);
      setSubmited(true);
      return;
    }
    const idx = Math.floor(Math.random() * roundItems.length);
    const item = roundItems[idx];
    const copy = [...roundItems];
    copy.splice(idx, 1);
    setRoundItems(copy);
    setCurrent(item);
    setSubmited(false);
  };

  const answer = (choice) => {
    if (!current) return;
    // prevent double-answering due to rapid clicks or countdown+click race
    if (answeringRef.current) return;
    answeringRef.current = true;
    setSubmited(true);
    setSelectedChoice(choice);
    const correct = Boolean(current.is_real) === Boolean(choice);
    setAnsweredCount(v => v + 1);
    if (correct) {
      setTotalCorrect(v => v + 1);
      setCorrectList(arr => [...arr, current]);
    } else {
      setTotalIncorrect(v => v + 1);
      setWrongList(arr => [...arr, current]);
    }
    setTimeout(() => {
      setSelectedChoice(null);
      nextItem();
      // allow answering again on the next question
      answeringRef.current = false;
    }, 500);
  };

  return (
    <div className="App bg-gray-900 w-full min-h-[60vh] flex items-center py-3 justify-center">
      {isStarted ? (
        current ? (
          <div className="px-10 w-full max-w-3xl">
            <div className="w-full flex justify-between items-center mt-3">
              <div className="flex items-center gap-3 text-white font-semibold"> 
                <div>Question {submited ? answeredCount : answeredCount + 1} of {totalQuestions}</div>
                <DifficultyBadge difficulty={current?.difficulty || selectedDifficulty} />
              </div>
              <ReactCountdownClock
                weight={10}
                seconds={!submited ? selectedSeconds : 0}
                color="#fff"
                size={80}
                paused={submited}
                onComplete={() => answer(false)}
              />
            </div>
            <div className="w-full text-center mt-6">
              <div className="flex items-center justify-center gap-3">
                <h1 className="text-4xl md:text-5xl text-white font-extrabold">Is this a real English word?</h1>
              </div>
              <div className="text-6xl md:text-7xl text-white font-extrabold my-10">{current.word}</div>
              <div className="flex gap-6 justify-center">
                <button
                  className={`px-10 py-4 rounded-xl text-2xl border-2 transition-colors duration-150 ${selectedChoice === true ? 'bg-green-600 border-green-600 text-white hover:bg-green-700' : 'bg-white/10 border-white text-white hover:bg-white/20'}`}
                  onClick={() => answer(true)}
                >
                  ✓ Yes
                </button>
                <button
                  className={`px-10 py-4 rounded-xl text-2xl border-2 transition-colors duration-150 ${selectedChoice === false ? 'bg-red-600 border-red-600 text-white hover:bg-red-700' : 'bg-white/10 border-white text-white hover:bg-white/20'}`}
                  onClick={() => answer(false)}
                >
                  ✕ No
                </button>
              </div>
            </div>
          </div>
        ) : current === undefined ? (
          isFullTest ? (
            // Do not show per-module results during a Full Test — advance immediately.
            <div className="px-6 w-full max-w-3xl text-center text-white">Continuing to next module…</div>
          ) : (
            <div className="px-6 w-full max-w-3xl">
              <h2 className="text-white text-3xl font-bold text-center">Results</h2>
              <div className="text-white text-center mt-2">Total: <span className="font-semibold">{totalQuestions}</span> · Correct: <span className="text-green-400 font-semibold">{totalCorrect}</span> · Incorrect: <span className="text-red-400 font-semibold">{totalIncorrect}</span></div>
              <div className="text-center mt-2 text-white">Score: <span className="font-bold">{totalQuestions ? Math.round((totalCorrect / totalQuestions) * 100) : 0}%</span></div>

              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                {correctList.length > 0 && (
                  <div>
                    <h3 className="text-green-400 text-xl font-semibold mb-2">Correct</h3>
                    <div className="flex flex-wrap gap-2">
                      {correctList.map((w, i) => (
                        <span key={`c-${i}`} className={`px-2 py-1 ${w.is_real ? 'bg-green-700' : 'bg-red-700'} text-white rounded`}>{w.word}</span>
                      ))}
                    </div>
                  </div>
                )}

                {wrongList.length > 0 && (
                  <div>
                    <h3 className="text-red-400 text-xl font-semibold mb-2">Incorrect</h3>
                    <div className="flex flex-wrap gap-2">
                      {wrongList.map((w, i) => (
                        <span key={`w-${i}`} className={`px-2 py-1 ${w.is_real ? 'bg-green-700' : 'bg-red-700'} text-white rounded`}>{w.word}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="w-full flex justify-center mt-6">
                <button className="mt-6 bg-blue-500 text-white p-2 px-6 cursor-pointer rounded-xl" onClick={() => setIsStarted(false)}>Play again</button>
              </div>
            </div>
          )
        ) : (
          <h1 className="text-3xl text-white">Loading...</h1>
        )
      ) : (
        <div className="flex flex-col items-center text-center px-5 md:px-0">
          <h1 className="text-4xl text-white font-bold mb-3 ">Read and Select</h1>
          <div className="flex flex-col items-center gap-3 text-white">
            <div>
              <span className="mr-2">Seconds:</span>
              <select className="text-black px-2 py-1 rounded" value={selectedSeconds} onChange={(e) => setSelectedSeconds(parseInt(e.target.value))}>
                <option value={5}>5</option>
                <option value={4}>4</option>
                <option value={3}>3</option>
              </select>
            </div>
            <div>
              <span className="mr-2">Difficulty:</span>
              <select className="text-black px-2 py-1 rounded" value={selectedDifficulty} onChange={(e) => setSelectedDifficulty(e.target.value)}>
                <option value="any">Any</option>
                <option value="basic">Basic</option>
                <option value="medium">Medium</option>
                <option value="advanced">Advanced</option>
              </select>
            </div>
          </div>
          <div className="mt-2 text-sm text-gray-300">Available exercises: {selectedDifficulty === 'any' ? allItems.length : allItems.filter(i => i.difficulty === selectedDifficulty).length}</div>
          <input
            type="submit"
            value="Start"
            className="mt-6 bg-green-500  text-white p-2 w-24 cursor-pointer rounded-xl"
            onClick={() => startRound()}
          />
        </div>
      )}
    </div>
  );
}

export default ReadAndSelectComp;


